import eventbus from 'tiny-emitter/instance';
import { SEAT } from '@/constants';

import { hypotenuse, getAngle, flipMatrix, calculateTheGap } from './utils';

import { Block } from '../models/block';
import { Row } from '../models/row';
import { Seat } from '../models/seat';

// const RADIUS_GAP = SEAT.INNER_RADIUS + SEAT.GAP;

/* eslint-disable no-unused-vars, no-plusplus */
export default class DrawingSeats {
  isDrawn = false;
  isDrawing = false;
  moved = false;
  drawingRow = false;
  clickCount = 0;
  precalculatedGaps = [];
  yposition = 0;
  precalculatedGapsCount = 50;

  constructor(app) {
    this.app = app;
    this.svg = this.app.svg;
    this.canvas = this.app.canvas;
    this.setup();
    this.addEventListeners();
  }

  // get finalSeatRadius() {
  //   return RADIUS_GAP * this.app.seatmap.scale;
  // }

  get seatXRadius() {
    return (SEAT.INNER_RADIUS + this.app.seatmap.lastUsedSeatSpacing) * this.app.seatmap.scale;
  }

  get seatYRadius() {
    return (SEAT.INNER_RADIUS + this.app.seatmap.lastUsedRowSpacing) * this.app.seatmap.scale;
  }

  get rowsCount() {
    return this.rows.length;
  }

  get seatsCountInFirstRow() {
    return this.rows[0].length;
  }

  get labelText() {
    let text = '';
    let tot = '';
    if (this.rowsCount > 1 || this.seatsCountInFirstRow > 1) {
      tot = this.rowsCount * this.seatsCountInFirstRow;
      text = `${this.rowsCount}x${this.seatsCountInFirstRow}  ${tot}`;
    }
    return text;
  }

  setup() {
    this.rows = [];
    this.svg.attr({ cursor: 'crosshair' });
    // this.precalculatedGaps = fillTheGaps(this.finalSeatRadius, this.precalculatedGapsCount);
    this.createCoords();
    this.drawInitialSeat();
    this.drawSeatCountLabel();
  }

  addEventListeners() {
    this.svg.on('mousedown', this.handleMouseDown, this);
    this.svg.on('mouseup', this.handleMouseUp, this);
    this.svg.on('mousemove', this.handleMouseMove, this);
  }

  removeEventListeners() {
    this.svg.off('mousedown', this.handleMouseDown, this);
    this.svg.off('mouseup', this.handleMouseUp, this);
    this.svg.off('mousemove', this.handleMouseMove, this);
  }

  handleMouseDown(e) {
    if (e.button !== 0) return;
    e.preventDefault();
    if (!this.isDrawing) {
      this.isDrawn = true;
      this.moved = false;
      this.isDrawing = true;
      this.yposition = 0;
      this.updateCoords(e);
    } else {
      this.yposition = this.coords.y2;
    }
    eventbus.emit('drawing:started');
  }

  handleMouseMove(e) {
    if (this.isDrawing) {
      const seatsCount = this.seatsCountInFirstRow;
      if (seatsCount !== 1) {
        this.moved = true;
      }
      if (this.yposition) {
        this.handleDrawingBlockDiagonalMove(e);
      } else {
        this.drawingRow ? this.handleDrawingRowMove(e) : this.handleDrawingBlockMove(e);
      }
      this.updateLabel();
    } else {
      this.moveCursor(e);
    }
  }

  handleMouseUp(e) {
    if (!this.isDrawing) return;
    this.clickCount += 1;
    if (this.clickCount === 1 && !this.moved) {
      this.isDrawing = true;
      this.drawingRow = true;
    } else if (this.clickCount === 2) {
      // finish drawing row
      this.clickCount = 0;
      this.isDrawing = false;
      this.drawingRow = false;
      this.handleDrawingEnd(e);
    } else {
      // finish drawing block
      this.isDrawing = false;
      this.clickCount = 0;
      this.handleDrawingEnd(e);
    }
  }

  handleDrawingBlockMove(e) {
    this.updateCoords(e, false);
    const lenX = Math.abs(this.coords.x2 - this.coords.x);
    const lenY = Math.abs(this.coords.y2 - this.coords.y);
    const rowCount = Math.trunc(lenY / this.seatYRadius);
    const seatsCount = Math.trunc(lenX / this.seatXRadius);
    const rowDiff = rowCount - (this.rowsCount - 1);
    if (rowDiff !== 0) {
      this.changeRowCount(rowDiff);
    }
    const seatsDiff = seatsCount - (this.seatsCountInFirstRow - 1);
    if (seatsDiff !== 0) {
      this.changeSeatsCountInRows(seatsDiff);
    }

    const angle = Math.round(getAngle(this.coords));
    this.flip(angle);
  }

  handleDrawingBlockDiagonalMove(e) {
    this.updateCoords(e, false);
    const lenY = Math.abs(this.coords.y2 - this.yposition);
    const rowCount = Math.trunc(lenY / this.seatYRadius);
    const length = hypotenuse(this.coords);
    const seatsCount = Math.trunc(length / this.seatXRadius);
    const rowDiff = rowCount - (this.rowsCount - 1);
    if (rowDiff !== 0) {
      this.changeRowCount(rowDiff);
    }
  }

  handleDrawingRowMove(e) {
    this.updateCoords(e, false);
    const length = hypotenuse(this.coords);
    const angle = Math.round(getAngle(this.coords));
    const numOfSeats = Math.trunc(length / this.seatYRadius);
    const seatsDiff = numOfSeats - (this.seatsCountInFirstRow - 1);
    if (seatsDiff > 0) {
      // need to add some seats
      this.addSeatsToRow(0, seatsDiff);
    } else if (seatsDiff < 0) {
      // need to remove some seats
      this.removeSeatsFromRow(0, Math.abs(seatsDiff));
    }

    if (this.seatsCountInFirstRow > 1) {
      this.rotate(angle, this.coords.x, this.coords.y);
    }
  }

  handleDrawingEnd(e) {
    const sid = this.app.currentSection && this.app.currentSection.uuid;
    const addBlockPromise = new Promise((resolve) => {
      const block = Block.create({ sid });
      block.show();
      this.rows.forEach((r) => {
        const row = Row.create({ block });
        r.forEach((shape) => {
          const cx = shape.attr('cx');
          const cy = shape.attr('cy');

          Seat.create({
            row,
            view: shape,
            attributes: { position: [cx, cy] },
          });
        });
        row.renderLabel(false);
        if (!this.app.seatmap.store.showLabels) {
          row.hideLabel();
        }
      });
      block.calculateLengthsCoeffs();
      resolve(block);
    });
    addBlockPromise.then((block) => {
      this.destroyDrawings();
      eventbus.emit('seatmap:add:block', block);

      // reassign section to caregory to update seat categories
      if (this.app.currentSection) {
        eventbus.emit('section:category:assign', this.app.currentSection, this.app.currentSection.categoryId);
      }
    });
  }

  rotate(angle, cx, cy) {
    this.group.transform({ rotation: angle, cx, cy });
  }

  flip(angle) {
    const tm = flipMatrix(angle, this.coords.x, this.coords.y);
    this.group.transform(tm);
  }

  moveCursor(e) {
    this.updateCoords(e);
    this.initialSeat.attr({ cx: this.coords.x, cy: this.coords.y });
  }

  createCoords() {
    this.coords = Object.create(null);
  }

  updateCoords(e, initial = true) {
    const { x, y } = this.app.getTransformedCoords(e);
    if (initial) {
      this.coords.x = x;
      this.coords.y = y;
    } else {
      this.coords.x2 = x;
      this.coords.y2 = y;
    }
  }

  drawSeatCountLabel(x = 0, y = 0) {
    this.label = this.app.svg
      .text(this.labelText)
      .font({
        size: 16,
        weight: 'bold',
        family: 'Roboto',
        style: 'normal',
      })
      .addClass('seat-count-label');
    let cx;
    let cy;
    let w;
    let h;
    if (this.app.isFirefox) {
      this.label
        .attr({
          'text-anchor': 'left',
          'dominant-baseline': 'central',
        })
        .center(x, y - 7);
      const { cx: cx1, cy: cy1, w: w1, h: h1 } = this.label.rbox();
      cx = cx1;
      cy = cy1;
      w = w1;
      h = h1;
    } else {
      this.label.dx(x).dy(y);
      const { cx: cx1, cy: cy1, w: w1, h: h1 } = this.label.rbox(this.svg);
      cx = cx1;
      cy = cy1;
      w = w1;
      h = h1;
    }
    this.labelBg = this.app.svg
      .rect(w + 5, h + 5)
      .attr('rx', 5)
      .attr('ry', 5)
      .addClass('seat-count-bg');
    this.labelBg.center(cx, cy + 7);
    this.countGroup = this.canvas.group();
    this.countGroup.add(this.labelBg).add(this.label);
    this.countGroup.front();
  }

  updateLabel() {
    const { cx, cy } = this.group.rbox(this.svg);
    this.label.text(this.labelText);
    const { w, h } = this.label.rbox(this.svg);
    this.labelBg.size(w + 5, h + 5);
    this.countGroup.center(cx, cy);
  }

  drawInitialSeat() {
    this.group = this.canvas.group().addClass('drawing-seats-group'); //.scale(this.app.seatmap.scale);
    this.initialSeat = this.drawSingleSeat().addTo(this.group);
    this.rows[0] = [this.initialSeat];
  }

  drawSingleSeat(cx = -10000, cy = -10000) {
    return this.group
      .circle(SEAT.INNER_RADIUS * this.app.seatmap.scale)
      .attr({ cx, cy, 'stroke-width': 1 * this.app.seatmap.scale });
  }

  changeRowCount(diff) {
    if (diff > 0) {
      this.addRows(diff);
    } else {
      this.removeRows(Math.abs(diff));
    }
    this.updateSeatsCountInRows();
  }

  addRow() {
    this.rows.push([]);
  }

  addRows(count) {
    let pos;
    let y;
    let gap;
    while (count--) {
      pos = this.rowsCount;
      // gap = this.precalculatedGaps[pos] || calculateTheGap(this.finalSeatRadius, pos);
      gap = calculateTheGap(this.seatYRadius, pos);
      y = this.coords.y + gap;
      this.rows.push([this.drawSingleSeat(this.coords.x, y)]);
    }
  }

  addSeatsToRow(rowIndex, count) {
    let gap;
    const row = this.getRow(rowIndex);
    const rowSeatsCount = row.length;
    const initialSeat = row[0];
    const cy = initialSeat.attr('cy');
    for (let i = rowSeatsCount, l = rowSeatsCount + count; i < l; i++) {
      // gap = this.precalculatedGaps[i] || calculateTheGap(this.finalSeatRadius, i);
      gap = calculateTheGap(this.seatXRadius, i);
      row.push(this.drawSingleSeat(this.coords.x + gap, cy));
    }
  }

  addSeatsToAllRows(count) {
    let len = this.rowsCount;
    while (len--) {
      this.addSeatsToRow(len, count);
    }
  }

  removeRows(count) {
    this.rows.splice(-count, count).forEach((row) => row.forEach((seat) => seat.remove()));
  }

  removeSeatsFromRow(rowIndex, count) {
    this.getRow(rowIndex)
      .splice(-count, count)
      .forEach((seat) => seat.remove());
  }

  removeSeatsFromAllRows(count) {
    let len = this.rowsCount;
    while (len--) {
      this.removeSeatsFromRow(len, count);
    }
  }

  updateSeatsCountInRows() {
    const seatsCount = this.seatsCountInFirstRow;
    this.rows.forEach((row, i) => {
      const diff = row.length - seatsCount;
      if (diff > 0) {
        this.removeSeatsFromRow(i, diff);
      } else if (diff < 0) {
        this.addSeatsToRow(i, Math.abs(diff));
      }
    });
  }

  changeSeatsCountInRows(diff) {
    if (diff > 0) {
      this.addSeatsToAllRows(diff);
    } else {
      this.removeSeatsFromAllRows(Math.abs(diff));
    }
  }

  removeLabel() {
    this.countGroup.remove();
  }

  destroyDrawings() {
    this.rows = [];
    this.group.remove();
    this.removeLabel();
    this.svg.attr({ cursor: 'default' });
  }

  getRow(rowIndex) {
    return this.rows[rowIndex];
  }

  getSeatInRow(rowIndex, seatIndex) {
    return this.getRow(rowIndex)[seatIndex];
  }

  getSeatsCountInRow(i) {
    return this.rows[i].length;
  }

  destroy() {
    this.removeEventListeners();
    if (!this.isDrawn || this.isDrawing) {
      this.destroyDrawings();
    }
  }
}
